/**
 * Trigger Notification Service
 * 
 * Client-side utility to trigger notifications via edge function.
 * Called after successful operations (timesheet approval, leave, swaps).
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TYPES
// ============================================================================

interface TimesheetNotificationPayload {
  type: 'timesheet_status';
  companyId: string;
  entryId: string;
  status: 'approved' | 'rejected';
  employeeProfileId: string;
  employeeName?: string;
}

interface LeaveSubmittedNotificationPayload {
  type: 'leave_submitted';
  companyId: string;
  leaveRequestId: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
  requesterProfileId: string;
  requesterName?: string;
}

interface LeaveStatusNotificationPayload {
  type: 'leave_status';
  companyId: string;
  leaveRequestId: string;
  status: string;
  requesterProfileId: string;
  requesterName?: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
}

interface SwapCreatedNotificationPayload {
  type: 'swap_created';
  companyId: string;
  swapId: string;
  shiftDate?: string;
  candidateProfileIds: string[];
  requesterName?: string;
  requesterShiftInfo?: string;
}

interface SwapResponseNotificationPayload {
  type: 'swap_response';
  companyId: string;
  swapId: string;
  response: 'accepted' | 'declined';
  requesterProfileId: string;
  responderProfileId: string;
  acceptedCandidateName?: string;
}

interface SwapApprovedNotificationPayload {
  type: 'swap_approved';
  companyId: string;
  swapId: string;
  approvalStatus: 'approved' | 'rejected';
  requesterProfileId: string;
  acceptedCandidateProfileId?: string;
  requesterName?: string;
  acceptedCandidateName?: string;
}

type NotificationPayload = 
  | TimesheetNotificationPayload
  | LeaveSubmittedNotificationPayload
  | LeaveStatusNotificationPayload
  | SwapCreatedNotificationPayload
  | SwapResponseNotificationPayload
  | SwapApprovedNotificationPayload;

// ============================================================================
// TRIGGER FUNCTION
// ============================================================================

/**
 * Trigger a notification via edge function
 * 
 * This is a fire-and-forget operation - we don't want notification
 * failures to block the main workflow. Errors are logged but not thrown.
 */
export async function triggerNotification(payload: NotificationPayload): Promise<void> {
  try {
    console.log('[triggerNotification] Sending:', payload.type);
    
    const { error } = await supabase.functions.invoke('generate-notification', {
      body: payload,
    });

    if (error) {
      console.error('[triggerNotification] Error:', error);
    } else {
      console.log('[triggerNotification] Success:', payload.type);
    }
  } catch (err) {
    // Don't throw - notifications should not block main operations
    console.error('[triggerNotification] Failed:', err);
  }
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Trigger notification for timesheet approval/rejection
 */
export function notifyTimesheetStatus(params: {
  companyId: string;
  entryId: string;
  status: 'approved' | 'rejected';
  employeeProfileId: string;
  employeeName?: string;
}): void {
  triggerNotification({
    type: 'timesheet_status',
    ...params,
  });
}

/**
 * Trigger notification for leave request submission
 */
export function notifyLeaveSubmitted(params: {
  companyId: string;
  leaveRequestId: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
  requesterProfileId: string;
  requesterName?: string;
}): void {
  triggerNotification({
    type: 'leave_submitted',
    ...params,
  });
}

/**
 * Trigger notification for leave request status change
 */
export function notifyLeaveStatus(params: {
  companyId: string;
  leaveRequestId: string;
  status: string;
  requesterProfileId: string;
  requesterName?: string;
  leaveType?: string;
  startDate?: string;
  endDate?: string;
}): void {
  triggerNotification({
    type: 'leave_status',
    ...params,
  });
}

/**
 * Trigger notification for swap request creation
 */
export function notifySwapCreated(params: {
  companyId: string;
  swapId: string;
  shiftDate?: string;
  candidateProfileIds: string[];
  requesterName?: string;
  requesterShiftInfo?: string;
}): void {
  triggerNotification({
    type: 'swap_created',
    ...params,
  });
}

/**
 * Trigger notification for swap response (accept/decline)
 */
export function notifySwapResponse(params: {
  companyId: string;
  swapId: string;
  response: 'accepted' | 'declined';
  requesterProfileId: string;
  responderProfileId: string;
  acceptedCandidateName?: string;
}): void {
  triggerNotification({
    type: 'swap_response',
    ...params,
  });
}

/**
 * Trigger notification for swap manager approval
 */
export function notifySwapApproved(params: {
  companyId: string;
  swapId: string;
  approvalStatus: 'approved' | 'rejected';
  requesterProfileId: string;
  acceptedCandidateProfileId?: string;
  requesterName?: string;
  acceptedCandidateName?: string;
}): void {
  triggerNotification({
    type: 'swap_approved',
    ...params,
  });
}
